import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode, type ReactElement } from 'react';

/**
 * Transient messages, announced rather than merely displayed.
 *
 * The live region is `polite` and always present in the DOM: a region created
 * at the moment a message appears is frequently not announced at all, because
 * the assistive technology never observed it becoming non-empty.
 *
 * Errors do not auto-dismiss. A success can vanish — the user saw the thing
 * succeed — but a failure that disappears before it is read is a failure the
 * user will not know about.
 */
export type ToastTone = 'success' | 'warning' | 'error' | 'info';

/** An optional affordance, e.g. "View job" on a completion toast (§05.8). */
export interface ToastAction {
  label: string;
  onClick(): void;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction | undefined;
}

interface ToastApi {
  toasts: readonly Toast[];
  push(tone: ToastTone, message: string, action?: ToastAction): void;
  dismiss(id: number): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 6_000;

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string, action?: ToastAction) => {
      const id = nextId++;
      setToasts((current) => [...current, { id, tone, message, action }]);
      if (tone !== 'error') {
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo<ToastApi>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost />
    </ToastContext.Provider>
  );
}

/**
 * The `-text` variants, not the fills.
 *
 * §16.4 draws this distinction for pills and it applies identically here: the
 * fill colours were measured under white, and `--color-danger` on
 * `--color-danger-soft` is 4.44:1 — under the 4.5:1 §16.6 requires. The token
 * that exists for ink on a soft background is the one that has been measured
 * against it.
 */
const TONE_CLASS: Record<ToastTone, string> = {
  success: 'border-success/20 bg-success-soft text-success-text',
  warning: 'border-warning/20 bg-warning-soft text-warning-text',
  error: 'border-danger/20 bg-danger-soft text-danger-text',
  info: 'border-line bg-surface text-body',
};

/**
 * The left rule, in the SOLID tone rather than the soft one.
 *
 * A stack of toasts is read down its left edge before any of it is read as
 * prose — three pale rectangles differing only in tint are one object at a
 * glance. The 2px rule is the same device the selected table row uses, and the
 * same argument: an edge is legible at a distance a fill is not.
 */
const TONE_RULE: Record<ToastTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
  info: 'bg-line-strong',
};

/**
 * Shape as well as hue (WCAG 1.4.1), and the same glyph vocabulary the status
 * pills use — `warning` and `error` measure ΔE 6.1 apart, so hue alone does not
 * separate them for anybody.
 */
const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleAlert,
  info: Info,
};

function ToastHost(): ReactElement {
  const { toasts, dismiss } = useToast();
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2"
    >
      {toasts.map((toast) => {
        const Icon = TONE_ICON[toast.tone];
        return (
          <div
            key={toast.id}
            /* `--elevation-2`, not `shadow-sm`. Tailwind's own shadow scale is a
               hard-coded colour that happens to slip past the source scan (it
               bans palette classes with a numeric suffix, and `sm` is not one),
               and a toast is precisely the object §16.2 wrote the elevation
               scale for: it floats above whatever the operator was reading. */
            className={`pointer-events-auto relative flex items-start gap-[var(--space-2)] overflow-hidden rounded-[var(--radius-md)] border p-3 pl-4 text-[length:var(--text-sm)] shadow-[var(--elevation-2)] ${
              TONE_CLASS[toast.tone]
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute inset-y-0 left-0 w-[2px] ${TONE_RULE[toast.tone]}`}
            />
            {/* Decorative: the message beside it is the announced content, and
                the live region reads the text, not the glyph. */}
            <Icon aria-hidden="true" size={14} strokeWidth={2.25} className="mt-[2px] shrink-0" />
            <span className="flex-1">{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick();
                  dismiss(toast.id);
                }}
                className="text-[length:var(--text-2xs)] font-medium underline underline-offset-2"
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="text-[length:var(--text-2xs)] underline underline-offset-2"
              aria-label="Dismiss message"
            >
              Dismiss
            </button>
          </div>
        );
      })}
    </div>
  );
}
